import { fmtBRL, fmtInt } from "@/lib/api";
import { fmtH } from "./pcShared";
import { TrendUp, TrendDown } from "@phosphor-icons/react";

function Delta({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${up ? "text-green-600" : "text-red-500"}`}>
      {up ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
      {Math.abs(pct)}%
    </span>
  );
}

function Kpi({ label, value, sub, delta, testid, accent }) {
  return (
    <div className="tile p-3" data-testid={testid}>
      <div className="overline text-[9px] text-zinc-500 truncate" title={label}>{label}</div>
      <div className={`font-mono text-xl font-bold leading-tight ${accent || ""}`}>{value}</div>
      <div className="flex items-center gap-2 min-h-[14px]">
        {sub && <span className="text-[10px] text-zinc-500">{sub}</span>}
        <Delta pct={delta} />
      </div>
    </div>
  );
}

export default function KpiGrid({ kpis, loading }) {
  if (loading || !kpis) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="pc-kpi-loading">
        {["a", "b", "c", "d", "e", "f", "g", "h"].map((k) => (
          <div key={`kpi-skeleton-${k}`} className="tile p-3 animate-pulse h-[72px]" />
        ))}
      </div>
    );
  }
  const q = kpis.quick;
  const p = kpis.period;
  return (
    <div className="space-y-2" data-testid="pc-kpi-grid">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["Produção Hoje", q.today], ["Produção Semana", q.week], ["Produção Mês", q.month], ["Produção Ano", q.year]].map(([label, v]) => (
          <div key={label} className="tile-muted p-3" data-testid={`pc-quick-${label}`}>
            <div className="overline text-[9px] text-zinc-500">{label}</div>
            <div className="font-mono text-lg font-bold">{fmtInt(v.pieces)} <span className="text-[10px] font-normal text-zinc-500">peças</span></div>
            <div className="text-[10px] text-zinc-500">{fmtInt(v.orders)} pedidos · {fmtBRL(v.value)}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="Pedidos Produzidos" value={fmtInt(p.orders_produced)} delta={p.vs_prev.orders_pct} testid="pc-kpi-orders" />
        <Kpi label="Peças Produzidas" value={fmtInt(p.pieces_produced)} delta={p.vs_prev.pieces_pct} testid="pc-kpi-pieces" />
        <Kpi label="SKUs Produzidos" value={fmtInt(p.skus_produced)} testid="pc-kpi-skus" />
        <Kpi label="Valor Produzido" value={fmtBRL(p.value_produced)} delta={p.vs_prev.value_pct} testid="pc-kpi-value" accent="text-green-600" />
        <Kpi label="Ticket Médio" value={fmtBRL(p.avg_ticket)} testid="pc-kpi-ticket" />
        <Kpi label="Tempo Médio Produção" value={fmtH(p.avg_production_hours)} sub="pedido→envio" testid="pc-kpi-avg-prod" />
        <Kpi label="Tempo Médio Etapa" value={fmtH(p.avg_stage_hours)} testid="pc-kpi-avg-stage" />
        <Kpi label="Eficiência (no prazo)" value={`${p.efficiency_pct}%`} testid="pc-kpi-efficiency" />
        <Kpi label="Em Andamento" value={fmtInt(p.orders_in_progress)} testid="pc-kpi-inprogress" />
        <Kpi label="Atrasados" value={fmtInt(p.orders_overdue)} accent={p.orders_overdue > 0 ? "text-red-500" : ""} testid="pc-kpi-overdue" />
        <Kpi label="Retrabalhos" value={fmtInt(p.reworks)} accent={p.reworks > 0 ? "text-yellow-600" : ""} testid="pc-kpi-reworks" />
        <Kpi label="Movimentos" value={fmtInt(p.movements)} testid="pc-kpi-movements" />
        <Kpi label="Colaboradores Ativos" value={fmtInt(p.active_collaborators)} testid="pc-kpi-collabs" />
        <Kpi label="Peças / Colaborador" value={fmtInt(p.productivity_per_collaborator)} testid="pc-kpi-prod-collab" />
        <Kpi label="Peças / Hora" value={fmtInt(p.pieces_per_hour)} testid="pc-kpi-pieces-hour" />
        <Kpi label="Valor Líquido" value={fmtBRL(p.net_value_produced)} testid="pc-kpi-net" />
      </div>
    </div>
  );
}
